import {
  Video,
  ExtraElementData as ExtraElementDataType,
} from '@src/types.ts/story';
import { Element } from './Element';
import {
  LayoutElementsResult,
  findLayoutRelatedElements,
} from '@src/utility/stackedLayout';
import { toJS } from 'mobx';
import { deepClone } from '@src/utility/deepClone';
import { ImageElement } from './elements/ImageElement';

export class ExtraElementData {
  // will be modified
  private _data: Video['extraElementData'];

  constructor(data: Video['extraElementData']) {
    this._data = data;
  }

  isLayoutChild(element: Element): boolean {
    const layoutElementsData = this.findLayoutRelatedElements(element);
    return layoutElementsData.isLayoutChild;
  }

  isLayoutParent(element: Element): boolean {
    const layoutElementsData = this.findLayoutRelatedElements(element);
    return layoutElementsData.isHiddenParent;
  }

  isLayoutElement(element: Element): boolean {
    const layoutElementsData = this.findLayoutRelatedElements(element);
    return (
      layoutElementsData.isHiddenParent || layoutElementsData.isLayoutChild
    );
  }

  findLayoutRelatedElements(element: Element): LayoutElementsResult {
    return findLayoutRelatedElements(undefined, element.id, this._data);
  }

  setLayoutElements(
    hiddenParent: Element,
    topElement: Element,
    bottomElement: Element,
    layoutType: 'stacked-1-1' | 'stacked-1-3',
  ): void {
    this._data[hiddenParent.id] = {
      ...this.getElementData(hiddenParent),
      layoutPosition: 'hidden',
      layoutAssociatedIds: [topElement.id, bottomElement.id],
      layoutType,
    };

    this._data[topElement.id] = {
      ...this.getElementData(hiddenParent),
      layoutPosition: 'top',
      layoutAssociatedIds: [hiddenParent.id],
      layoutType,
    };

    this._data[bottomElement.id] = {
      ...this.getElementData(hiddenParent),
      layoutPosition: 'bottom',
      layoutAssociatedIds: [hiddenParent.id],
      layoutType,
    };
  }

  /**
   * Removes data for an element from extraElementData
   * @param elementId ID of the element to remove data for
   */
  removeElementData(elementId: string): void {
    if (this._data[elementId]) {
      delete this._data[elementId];
    }
  }

  clearLayoutData(hiddenParent: Element): void {
    const eed = this.getElementData(hiddenParent);
    if (eed) {
      delete eed.layoutAssociatedIds;
      delete eed.layoutPosition;
      delete eed.layoutType;
    }
  }

  getElementData(element: Element): ExtraElementDataType | undefined {
    return this._data[element.id] as ExtraElementDataType;
  }

  get value(): Video['extraElementData'] {
    return this._data;
  }

  cloneValue(): Video['extraElementData'] {
    return deepClone(toJS(this._data));
  }

  restoreValue(snapshot: Video['extraElementData']): void {
    for (const key of Object.keys(this._data)) {
      delete this._data[key];
    }
    for (const [key, value] of Object.entries(snapshot)) {
      this._data[key] = value;
    }
  }

  isMarkedAsLogo(element: ImageElement): boolean {
    return !!(this._data[`logo_el_${element.id}`] as ExtraElementDataType)
      ?.isLogo;
  }
}
